import React, { useEffect, useState } from "react";
import DynamicModal from "./DynamicModalForm";
import { getOptionText, getOptionValueByText, toOptionData } from "../../utils/optionUtils";
import { useDispatch } from "react-redux";
import { search } from "../../redux/searchSlice";
import { fetchTermDepositsInterest } from "../../redux/masters/termDepositSlice";
import { calculateInterest } from "../../utils/interestCalculator";
import { focus, calculateAge } from "../../utils/util";
import { colors } from "../../theme/theme.js";
import { Button, Box, InputAdornment, TextField } from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import FileUploadPopup from "../common/FileUploadPopup";

const initialForm = {
  depositNumberPrefix: "SOC/",
  depositNumber: "",
  depositNumberSuffix: "/2025",
  fkTermDepositId: "",
  fkMemberId: "",
  memberDetails: "",
  openingDate: "",
  depositAmount: "0",
  depositFrequency: "",
  depositType: "",
  depositDuration: "",  // now will hold selected id
  interestRate: "0",
  interestType: "",
  interestFrequency: "",
  interestAmount: "0",
  totalDepositAmount: "0",
  totalInterestAmount: "0",
  maturityDate: "",
  status: "",
  maturityAmount: "0",
  nomineeNo: "",
  nomineeName: "",
  nomineeRelation: "",
  nomineeAddress: "",
  nomineeMobile: "",
  nomineeDob: "",
  nomineeAge: "",
  nomineePanNo: "",
  nomineePanFiles: [],
  nomineeAadharNo: "",
  nomineeAadharFiles: [],
  narration: "",
  minAmount: "",
  maxAmount: "",
  minDuration: "",
  maxDuration: "",
  withReceipt: false,
  depositAmountAdornment: "Monthly | ₹",
  interestRateAdornment: "Flat |",
  interestFrequencyAdornment: "--- | ₹",
}

const AddDepositAccountModal = ({
  optionData = [],
  optionLoading,
  optionError,
  open,
  onClose,
  onSave,
  data,
}) => {
  const dispatch = useDispatch();
  const [interestOptions, setInterestOptions] = useState([]);
  const [fileUploadPopup, setFileUploadPopup] = useState({
    open: false,
    field: null,
    title: "",
    existingFiles: [],
    onSave: null,
  });

  const handleOpenFileUpload = (field, title) => {
    // Basic validation if needed
    if (!field) return;

    setFileUploadPopup({
      open: true,
      field,
      title,
      existingFiles: [], // In this context, we might not have existing files on server yet, or we'd strict load them.
      // For a simple implementation, we can pass current form files
    });
  };

  const handleCloseFileUpload = () => {
    setFileUploadPopup({
      open: false,
      field: null,
      title: "",
      existingFiles: [],
    });
  };

  const handleFilesSelected = (files) => {
     // files is an array of objects { file: File, ... }
     const { field } = fileUploadPopup;
     if (field) {
        setFields(prevFields => {
             // Force re-render of the specific field to update badge color if we were using it there
             // But here we are using form state.
             return prevFields;
        });
        
        // Update form state with new files
        // We accumulate files or replace? usually append.
        // But the popup returns *all* selected files usually? 
        // Let's verify MemberForm logic:
        /* 
           MemberForm filters new files and uploads them. 
           Here we might keep them in client state until save.
           Or if we want to mimic MemberForm exactly, we'd need to upload individually.
           However, given "Add Deposit", we likely don't have a record ID yet.
           So we store them in state.
        */
        const newFiles = files.filter(f => f.file).map(f => f.file);
        
        // Using onSave prop to update form 
        // Actually, let's just update the form directly
        onSaveFileUpload(files, field);
     }
  };

  const onSaveFileUpload = (files, field) => {
      // files is array of { file, path, etc }
      // We want to store the actual File objects or the structure expected by backend
      // For now, let's store the array of file objects/structures
      
      // Update form
      // We need to access setForm. However, setForm is internal to DynamicModal.
      // DynamicModal doesn't expose setForm to parent easily unless we use the context or props.
      // Wait, 'AddDepositAccountModal' receives 'data' and 'onSave', but 'form' state is INSIDE DynamicModal.
      // THIS IS A PROBLEM.
      // 'AddDepositAccountModal' is a wrapper around 'DynamicModal'.
      // 'DynamicModal' manages the form state.
      // 'fields' definition is inside 'AddDepositAccountModal'. 
      // The 'endAdornment' function has access to 'form'.
      
      // But 'handleOpenFileUpload' is defined in 'AddDepositAccountModal'.
      // When files are selected, we need to update the form state inside 'DynamicModal'.
      // 'DynamicModal' does not expose a method to update form from outside.
      
      // SOLUTION:
      // We can pass a context or ref to DynamicModal to allow external updates?
      // OR, better:
      // The 'fields' configuration allows 'onChange' or custom logic.
      // But the popup is outside.
      
      // Let's check DynamicModal usages. 
      // It passes 'context' to field renderers.
      // We can pass 'setFileUploadPopup' and 'fileUploadPopup' in context?
      // No, we need to pass a callback to UPDATE the form.
      
      // We can pass `handleFileUpload` to `DynamicModal` via `context`?
      // 'DynamicModal' takes `context` prop.
      // Inside `DynamicModal`, `renderField` passes `context` to `onChange` etc.
      
      // But `handleFilesSelected` is called by `FileUploadPopup`, which is outside `DynamicModal`.
      // It doesn't have access to `setForm`.
      
      // STRATEGY ADJUSTMENT:
      // We need `DynamicModal` to lift state up OR expose a way to set state.
      // Currently `DynamicModal` has `[form, setForm] = useState(initialForm)`.
      
      // If I cannot change `DynamicModal` significantly to lift state (as it might break other things),
      // I can add a `ref` to `DynamicModal` or pass a `setForm` callback in a ref?
      
      // Alternative: define `form` in `AddDepositAccountModal` and pass it to `DynamicModal`?
      // `DynamicModal` expects `initialForm`.
      
      // Let's look at `DynamicModal` again. 
      // It does `const [form, setForm] = useState(initialForm);`
      
      // Modification to `DynamicModal`:
      // Allow `form` and `setForm` to be controlled props?
      // Or add `onFormChange` prop?
      
      // EASIER FIX:
      // In `AddDepositAccountModal`, we define a mutable ref `formRef`.
      // We pass `formRef` in `context`.
      // Inside `DynamicModal`, we update `formRef.current` whenever form changes? No, that doesn't trigger re-render.
      // We can pass a "registerSetForm" callback in props?
      
      // Let's check `DynamicModalForm.jsx` again.
      // It has `context = {}`.
      // It passes `context` to `field.onChange`... but NOT to `field.endAdornment`.
      // The `endAdornment` takes `form`.
      
      // Wait, `DynamicModal` renders `FileUploadPopup`?
      // No, `AddDepositAccountModal` renders `DynamicModal`.
      // If `AddDepositAccountModal` renders `FileUploadPopup`, `FileUploadPopup` needs to update `DynamicModal`'s state.
      
      // REQUIRED CHANGE in `DynamicModalForm.jsx`:
      // Add `renderExtra` prop or just `children` to render content inside the modal (e.g. popups).
      // AND expose `setForm` to the content?
      
      // OR:
      // Move `FileUploadPopup` INSIDE `DynamicModalForm.jsx`? No, too specific.
      
      // CLEANEST APPROACH:
      // 1. Modify `DynamicModalForm.jsx` to accept `outerContent` or `children` that renders *alongside* the main content but has access to `form`/`setForm`?
      // 2. Or simply accept `children` that renders inside the Dialog.
      // 3. And importantly, pass `setForm` to the `context`.
      
      // Let's modify `DynamicModalForm.jsx` first to pass `setForm` in context, and accept generic children.
      
  };

  const calculateMaturityDate = (startDate, durationMonths) => {
    if (!startDate || !durationMonths) return "";
    const date = new Date(startDate);
    if (isNaN(date.getTime())) return "";
    date.setMonth(date.getMonth() + Number(durationMonths));
    return date.toISOString().split('T')[0];
  };
  
  const validateForm = (form) => {
    const newErrors = {};
    const requiredFields = [
      "fkTermDepositId",
      "fkMemberId",
      "openingDate",
      "depositAmount",
      "depositDuration",
      "interestRate",
      "maturityDate",
      "maturityAmount",
      "status"
    ];

    const fieldLabels = {
      depositNumberPrefix: "Prefix",
      depositNumber: "Deposit Number",
      depositNumberSuffix: "Suffix",
      fkTermDepositId: "Deposit Scheme",
      fkMemberId: "Member",
      memberDetails: "Member Details",
      openingDate: "Opening Date",
      depositAmount: "Deposit Amount",
      depositFrequency: "Deposit Frequency",
      depositDuration: "Duration (months)",
      interestRate: "ROI",
      interestType: "Interest Type",
      interestFrequency: "Interest Amount",
      interestAmount: "Interest",
      totalDepositAmount: "Total Deposit",
      totalInterestAmount: "Total Interest",
      maturityDate: "Maturity Date",
      maturityAmount: "Maturity Amount",
      nomineeNo: "Nominee No",
      nomineeName: "Nominee Name",
      nomineeRelation: "Relation",
      nomineeAddress: "Address",
      nomineeMobile: "Mobile",
      nomineePanNo: "Nominee PAN No.",
      nomineePanFiles: "Nominee PAN Files",
      nomineeAadharNo: "Nominee Aadhaar No.",
      nomineeAadharFiles: "Nominee Aadhaar Files",
      nomineeDob: "DOB",
      nomineeAge: "Age",
      narration: "Narration",
      status: "Status",
      withReceipt: "With Receipt",
    };

    const numericFields = [
      "depositAmount",
      "interestRate",
      "depositDuration",
      "maturityAmount"
    ];

    requiredFields.forEach((field) => {
      if (!form[field]?.toString().trim()) {
        newErrors[field] = `${fieldLabels[field] || field} is required`;
      }
    });

    numericFields.forEach((field) => {
      if (form[field] === "" || isNaN(Number(form[field])) || Number(form[field]) <= 0) {
        newErrors[field] = `${fieldLabels[field] || field} must be greater than 0 and a valid number`;
      }
    });

    return newErrors;
  };

  useEffect(() => {
  if (interestOptions.length > 0) {
    const depositDurations = toOptionData(interestOptions, "depositDuration", "id", "termDuration", "termDuration", "interestRate");
    setFields(prevFields =>
      prevFields.map(field =>
        field.name === "depositDuration" ? { ...field, options: depositDurations.values} : field
      )
    );
  }
}, [interestOptions]);
  
  const handleDepositDurationChange = (value, form, setForm, fields) => {
    setFields(currentFields => {
      const durationField = currentFields.find(f => f.name === "depositDuration");
      const selected = durationField.options.find(opt => opt.optionValue === value);
      if (selected) {
        const calculation = calculateInterest({
          depositAmount : Number(form.depositAmount),
          durationMonths : Number(selected.optionValue),
          depositType: form.depositType === "Recurring" ? "periodic" : "one-time",
          depositFrequency: form.depositFrequency,
          interestType: form.interestType,
          interestFrequency: form.interestFrequency,
          annualInterestRate: selected.data
        });
        setForm(prev => ({
          ...prev,
          depositDuration: selected.optionValue,
          interestRate: selected.data,
          totalInterestAmount: Math.round(Number(calculation.totalInterest)).toString(),
          interestAmount: Math.round(Number(calculation.averageInterest)).toString(),
          totalDepositAmount: Math.round(Number(calculation.totalDeposit)).toString(),
          maturityAmount: Math.round(Number(calculation.maturityAmount)).toString(),
          maturityDate: calculateMaturityDate(form.openingDate, selected.optionValue)
        }));
      }
      return currentFields;
    });
  };

  const mapFormValues = (form, optionData) => {
    const mappings = [
      { field: "fkTermDepositId", optionName: "Deposit Type" },
      { field: "nomineeRelation", optionName: "Relation" },
      { field: "status", optionName: "Term Deposit Status" }
    ];

    const mappedForm = { ...form };
    mappings.forEach(({ field, optionName }) => {
      if (mappedForm[field]) {
        mappedForm[field] = getOptionText(optionData, optionName, mappedForm[field]);
      }
    });
    return mappedForm;
  };

  const mapOptionValues = (data, optionData) => {
    const depositTypeValue = getOptionValueByText(
      optionData,
      "fkTermDepositId",
      data.fkTermDepositId
    );
    const nomineeRelationValue = getOptionValueByText(
      optionData,
      "Relation",
      data.nomineeRelation
    );
    const statusValue = getOptionValueByText(
      optionData,
      "Term Deposit Status",
      data.status
    );
    
    return {
      depositNumberPrefix: data.depositNumberPrefix || "SOC/",
      depositNumber: data.depositNumber || "",
      depositNumberSuffix: data.depositNumberSuffix || "/2025",
      fkTermDepositId: data.depositTypeValue || "",
      fkMemberId: data.fkMemberId || "",
      memberDetails: data.memberDetails || "",
      openingDate: data.openingDate || "",
      depositAmount: data.depositAmount || "0",
      depositFrequency: data.depositFrequency || "",
      depositType: data.depositFrequency || "",
      depositDuration: data.depositDuration || "",
      interestRate: data.interestRate || "0",
      interestType: data.interestType || "",
      interestFrequency: data.interestFrequency || "",
      interestAmount: data.interestAmount || "0",
      totalDepositAmount: data.totalDepositAmount || "0",
      totalInterestAmount: data.totalInterestAmount || "0",
      maturityDate: data.maturityDate || "",
      maturityAmount: data.maturityAmount || "0",
      nomineeNo: data.nomineeNo || "",
      nomineeName: data.nomineeName || "",
      nomineeRelation: data.nomineeRelationValue || "",
      narration: data.narration || "",
      status: data.status || "",
      withReceipt: data.withReceipt || false,
    };
  };
  const [fields, setFields] = useState([
    { name: "depositNumber", label: "Deposit Number", section: "depositAccountDetails", size: { xs: 6, sm: 6, md:3 }, backgroundColor: "#fbfaf1ff", adornment: (form) => form.depositNumberPrefix, endAdornment: (form) => form.depositNumberSuffix },
    {
      name: "fkTermDepositId",
      label: "Deposit Scheme",
      section: "depositAccountDetails",
      type: "select",
      optionName: "fkTermDepositId",
      onBlur: (e, { form, setForm, optionData }) => {
        const selected = optionData
          .find((opt) => opt.optionName === "fkTermDepositId")
          ?.values.find((val) => val.optionValue === form.fkTermDepositId);

        if (selected) {
          dispatch(fetchTermDepositsInterest(selected.id)).then((res) => {
            const data = res.payload || [];
            setInterestOptions(data); // store fetched interest options
            const calculation = calculateInterest({
              depositAmount : Number(selected.minDepositAmount),
              durationMonths : Number(selected.minDepositDuration),
              depositType: selected.depositType === "Recurring" ? "periodic" : "one-time",
              depositFrequency: selected.depositFrequency,
              interestType: selected.interestType,
              interestFrequency: selected.interestFrequency,
              annualInterestRate: data.length ? data[0].interestRate : 0
            });

            setForm((prev) => ({
              ...prev,
              depositType: selected.depositType || "",
              depositFrequency: selected.depositFrequency || "",
              interestType: selected.interestType || "",
              depositAmountAdornment: `${selected.depositFrequency} | ₹`,
              interestRateAdornment: `${selected.interestType} |`,
              interestFrequency: selected.interestFrequency,
              interestFrequencyAdornment: selected.interestFrequency ? `${selected.interestFrequency} | ₹` : "--- | ₹",
              depositAmount: selected.minDepositAmount,
              depositDuration: data.length ? data[0].termDuration : "",
              minAmount: selected.minDepositAmount,
              maxAmount: selected.maxDepositAmount,
              minDuration: selected.minDepositDuration,
              maxDuration: selected.maxDepositDuration,
              totalInterestAmount: Math.round(Number(calculation.totalInterest)).toString(),
              totalDepositAmount: Math.round(Number(calculation.totalDeposit)).toString(),
              maturityAmount: Math.round(Number(calculation.maturityAmount)).toString(),
              interestAmount: Math.round(Number(calculation.averageInterest)).toString(),
              interestRate: data.length ? data[0].interestRate : "0"
            }));
          });
        }
      },
      size: { xs: 12, sm: 12, md:5 }
    },

    { 
      name: "openingDate", 
      label: "Deposit Date", 
      section: "depositAccountDetails", 
      type: "date", 
      size: { xs: 12, sm: 12, md:4 },
      onChange: (e, { form, setForm }) => {
        const newDate = e.target.value;
        const maturityDate = calculateMaturityDate(newDate, form.depositDuration);
        setForm(prev => ({
          ...prev,
          openingDate: newDate,
          maturityDate: maturityDate
        }));
      }
    },

    { name: "fkMemberId", groupName: "memberDetails", section: "depositAccountDetails", label: "Member No", size: { xs: 12, sm: 12, md:2 }, targetGroups: ["nomineeDetails","depositDetails"], backgroundColor: "#fbfaf1ff",
      onBlur: (e, { setForm, dispatch, field }) => {
        const memberNo = e.target.value;
        console.log(e.target);
        if (!memberNo) return;

        dispatch(search({
          module: "Member",
          searchBy: "Member Number",
          searchValue: memberNo
        })).then((res) => {
          let data = res.payload?.data;
          let fullName = "";
          if (res.meta.requestStatus === "fulfilled" && data) {
            fullName = [
              data?.nameTitle,
              data?.fullName,
              data?.soWoCo,
              data?.fatherTitle,
              data?.fatherName
            ].filter(Boolean).join(" ");
            setFields(prevFields =>
              prevFields.map(f =>
                // dynamically check if field belongs to any of the target groups
                field.targetGroups?.includes(f.groupName)
                  ? { ...f, disabled: false }
                  : f
              )
            );
          }
          setForm((prev) => ({ ...prev, memberDetails: fullName }));
          focus("depositAmount");
        });
      }
    },

    { name: "memberDetails", groupName: "memberDetails", section: "depositAccountDetails", label: "Member Details", size: { xs: 12, sm: 12, md:10 }, readOnly: true },

    // Deposit Amount
    { name: "depositAmount", groupName: "depositDetails", section: "depositDetails", disabled:true, label: "Deposit Amount", type: "number",
      helper: (form) => form.minAmount ? `Min. ₹ ${form.minAmount} - Max. ₹ ${form.maxAmount}` : '', adornment: (form) => form.depositAmountAdornment || "Monthly | ₹", size: { xs: 12, sm: 12, md:4 },
      onBlur: (e, { form, setForm, optionData, dispatch, field}) => {
        const updatedForm = { ...form, depositAmount: Number(e.target.value) };
        handleDepositDurationChange(updatedForm.depositDuration, updatedForm, setForm, fields);
      }
    },

    // Deposit Duration (select)
    {
      name: "depositDuration", groupName: "depositDetails", section: "depositDetails",
      disabled:true, 
      label: "Duration (months)",
      type: "select",
      options: [],
      onChange: (e, { form, setForm }) => {
        handleDepositDurationChange(e.target.value, form, setForm, fields);
      }, 
      size: { xs: 12, sm: 12, md:4 }
    },


    { name: "totalDepositAmount", groupName: "depositDetails", section: "depositDetails", disabled:true, label: "Total Deposit", type: "number", adornment: "₹", size: { xs: 12, sm: 12, md:4 }, readOnly: true },
    { name: "interestRate", groupName: "depositDetails", section: "depositDetails", disabled:true, label: "Interest Rate (%)", type: "number", adornment: (form) => form.interestRateAdornment || "Flat |", size: { xs: 12, sm: 12, md:4 }, readOnly: true },
    { name: "interestAmount", groupName: "depositDetails", section: "depositDetails", disabled:true, label: "Interest Amount", size: { xs: 12, sm: 12, md:4 }, readOnly: true, adornment: (form) => form.interestFrequencyAdornment },
    { name: "totalInterestAmount", groupName: "depositDetails", section: "depositDetails", disabled:true, label: "Total Interest", type: "number", adornment: "₹", size: { xs: 12, sm: 12, md:4 }, readOnly: true },

    
    { name: "maturityDate", groupName: "depositDetails", section: "depositDetails", disabled:true, readOnly: true, label: "Maturity Date", type: "date", size: { xs: 12, sm: 12, md:4 } },
    { name: "maturityAmount", groupName: "depositDetails", section: "depositDetails", disabled:true, label: "Maturity Amount", type: "number", adornment: "₹", size: { xs: 12, sm: 12, md:4 }, readOnly: true },
    { name: "status", groupName: "depositDetails", section: "depositDetails", disabled:true, label: "Status", type: "select", optionName: "Term Deposit Status", size: { xs: 12, sm: 12, md:4 } },

    { name: "nomineeName", disabled:true, label: "Nominee Name / Member No", size: { xs: 12, sm: 12, md:8 }, groupName: "nomineeDetails", section: "nomineeDetails",
      onBlur: (e, { setForm, dispatch }) => {
        const value = e.target.value;
        if (!value) return;

        // Try to search as Member Number first
        dispatch(search({
          module: "Member",
          searchBy: "Member Number",
          searchValue: value
        })).then((res) => {
          let data = res.payload?.data;
          
          if (res.meta.requestStatus === "fulfilled" && data) {
            // FOUND: Populate details from member data
            const fullName = [
              data?.nameTitle,
              data?.fullName,
              data?.soWoCo,
              data?.fatherTitle,
              data?.fatherName
            ].filter(Boolean).join(" ");
            
            // Try different keys for mobile number
            const mobile = data?.mobileNo || data?.mobileNumber || data?.primaryMobile || "";
            const dob = data?.dob;
            let formattedDob = "";

            // Convert DD/MM/YYYY to YYYY-MM-DD for date input
            if (dob && dob.includes('/') && dob.split('/').length === 3) {
                const parts = dob.split('/');
                // Assuming dd/mm/yyyy
                const day = parts[0];
                const month = parts[1];
                const year = parts[2];
                formattedDob = `${year}-${month}-${day}`;
            }

            const age = data?.age || "";
            
            // Construct address from member data
            let address = "";
            const primaryAddr = data?.addresses?.find(a => a.isDefault) || data?.addresses?.[0];
            if (primaryAddr) {
                address = [
                    primaryAddr.addressLine1,
                    primaryAddr.addressLine2,
                    primaryAddr.landmark,
                    primaryAddr.city,
                    primaryAddr.state,
                    primaryAddr.pincode
                ].filter(Boolean).join(", ");
            }
            
             setForm((prev) => ({ 
                ...prev, 
                nomineeNo: data.memberNo || data.memberNumber || value, // Link to member number
                nomineeName: fullName || value,
                nomineeMobile: mobile,
                nomineeAddress: address,
                nomineeDob: formattedDob,
                nomineeAge: age
                // We don't overwrite other fields unless necessary
            }));
          } else {
            // NOT FOUND: Treat as manual entry
            // Just ensure nomineeNo is cleared (or set to null) to avoid linking to wrong member
             setForm((prev) => ({ 
                ...prev, 
                nomineeNo: "", // Clear link
                // preserve typed name as nomineeName
                // preserve other fields as they were (user might be typing)
            }));
          }
        });
      }
    },
    { name: "nomineeMobile", disabled:true, label: "Mobile", size: { xs: 12, sm: 12, md:4 }, groupName: "nomineeDetails", section: "nomineeDetails" },
    { name: "nomineeRelation", disabled:true, label: "Relation", type: "select", optionName: "Relation", size: { xs: 12, sm: 12, md:4 }, groupName: "nomineeDetails", section: "nomineeDetails" },
    { 
      name: "nomineeDob", 
      disabled:true, 
      label: "DOB", 
      type: "date", 
      size: { xs: 12, sm: 12, md:4 }, 
      groupName: "nomineeDetails", 
      section: "nomineeDetails",
      onChange: (e, { form, setForm }) => {
        const newDob = e.target.value;
        const age = calculateAge(newDob);
        setForm(prev => ({ 
            ...prev, 
            nomineeDob: newDob, 
            nomineeAge: age 
        }));
      }
    },
    { name: "nomineeAge", disabled:true, label: "Age (years)", type: "number", size: { xs: 12, sm: 12, md:4 }, groupName: "nomineeDetails", section: "nomineeDetails" },
    { name: "nomineeAddress", disabled:true, label: "Address", multiline: true, rows: 2, size: { xs: 12, sm: 12, md:12 }, groupName: "nomineeDetails", section: "nomineeDetails" },
    {
      name: "nomineeAadhaarNo",
      groupName: "nomineeDetails",
      section: "nomineeDetails",
      label: "Nominee Aadhaar No.",
      size: { xs: 12, sm: 12, md: 6 },
      type: "custom",
      disabled: true,
      render: (field, form, handleChange) => (
        <TextField
          fullWidth
          size="small"
          label={field.label}
          value={form[field.name] || ""}
          onChange={handleChange}
          name={field.name}
          disabled={field.disabled} // Always disabled as per requirement
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ margin: 0 }}>
                <Button
                  onClick={() => handleOpenFileUpload("nomineeAadharFiles", "Nominee Aadhaar")}
                  variant="contained"
                  disabled={field.disabled}
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                    minWidth: "auto",
                    color: "white",
                    px: 1,
                    height: "100%",
                    backgroundColor:
                      form.nomineeAadharFiles && form.nomineeAadharFiles.length > 0
                        ? colors.success[500]
                        : colors.error[500],
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CloudUpload sx={{ fontSize: "1.5rem" }} />
                </Button>
              </InputAdornment>
            ),
            sx: {
              paddingRight: 0,
            },
          }}
          sx={{
            backgroundColor: field.backgroundColor,
            "& .MuiOutlinedInput-root": {
              paddingRight: 0,
            },
          }}
        />
      )
    },
    // Pan
    {
      name: "nomineePanNo",
      groupName: "nomineeDetails",
      section: "nomineeDetails",
      label: "Nominee PAN No.",
      size: { xs: 12, sm: 12, md: 6 },
      type: "custom",
      disabled: true,
      render: (field, form, handleChange) => (
         <TextField
          fullWidth
          size="small"
          label={field.label}
          value={form[field.name] || ""}
          onChange={handleChange}
          name={field.name}
          disabled={field.disabled}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ margin: 0 }}>
                <Button
                  onClick={() => handleOpenFileUpload("nomineePanFiles", "Nominee PAN")}
                  variant="contained"
                  disabled={field.disabled}
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                    minWidth: "auto",
                    color: "white",
                    px: 1,
                    height: "100%",
                    backgroundColor:
                      form.nomineePanFiles && form.nomineePanFiles.length > 0
                        ? colors.success[500]
                        : colors.error[500],
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CloudUpload sx={{ fontSize: "1.5rem" }} />
                </Button>
              </InputAdornment>
            ),
             sx: {
              paddingRight: 0,
            },
          }}
           sx={{
            backgroundColor: field.backgroundColor,
            "& .MuiOutlinedInput-root": {
              paddingRight: 0,
            },
          }}
        />
      )
    },
    { name: "narration", disabled:true, label: "Narration", multiline: true, rows: 2, size: { xs: 12, sm: 12, md:12 }, groupName: "depositDetails", section: "depositDetails" },
  ]);

  const sections = [
    {
        title: "Deposit Account Details",
        gridSize: { xs: 12, sm: 12, md: 12 },
        fields: fields.filter(f => f.section === "depositAccountDetails"),
    },
    {
        title: "Deposit Details",
        gridSize: { xs: 12, sm: 12, md: 7 },
        fields: fields.filter(f => f.section === "depositDetails"),
        cardSx: {
            width: '100%',
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? `${theme.palette.success.main}08` : `${theme.palette.success.main}0A`,
            borderTop: (theme) => `3px solid ${theme.palette.success.main}66`
        },
        titleSx: {
            color: (theme) => theme.palette.success.main
        }
    },
    {
        title: "Nominee Details",
        gridSize: { xs: 12, sm: 12, md: 5 },
        fields: fields.filter(f => f.section === "nomineeDetails"),
        cardSx: {
            width: '100%',
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? `${colors.accent[500]}08` : `${colors.accent[500]}0A`,
            borderTop: (theme) => `3px solid ${colors.accent[500]}66`
        },
        titleSx: {
            color: (theme) => colors.accent[500]
        }
    }
  ];

  return (
    <DynamicModal
      open={open}
      onClose={onClose}
      onSave={onSave}
      data={data ? mapOptionValues(data, optionData) : null}
      title={data ? "Edit Deposit Account" : "Add Deposit Account"}
      sections={sections}
      initialForm={initialForm}
      validateForm={validateForm}
      mapFormValues={mapFormValues}
      optionData={optionData}
      context={{ dispatch, optionData }}
      saveButtonText={data ? "Update Deposit Account" : "Save Deposit Account"}
    >
      {({ setForm }) => (
        <FileUploadPopup
          open={fileUploadPopup.open}
          onClose={handleCloseFileUpload}
          onFilesSelected={(files) => {
            const { field } = fileUploadPopup;
            if (field) {
              // Extract files if they are wrapped in objects, or use directly
              // FileUploadPopup usually returns [{ file, path, ... }]
              // memberForm.jsx uses: const newFiles = selectedFiles.filter(...)
              
              // We just want to store the array of file objects in the form state
              // The `FileUploadPopup` manages the array.
              
              setForm(prev => ({
                 ...prev,
                 [field]: files
              }));
            }
            handleCloseFileUpload();
          }}
          title={fileUploadPopup.title}
          existingFiles={fileUploadPopup.existingFiles || []}
          maxFiles={6}
          accept="image/*"
          multiple={true}
        />
      )}
    </DynamicModal>
  );
};

export default AddDepositAccountModal;